/*
 *  Prepares the HTML email preview using a nested iframe approach for isolation and control.
 *
 *  1. Reset all extra styles/paddings/backgrounds remove scrollbars, that are needed for the proper design
 *     Injects specific CSS overrides for `.engage-v2-body` background/padding to match our theme
 *  2. Forces the selected theme (light/dark) by setting a `data-mode` attribute on the email's
 *     HTML, independent of the OS's theme (OS meta tag is completely removed).
 *  3. Dynamically calculates the email content's height and adjusts iframe heights
 *     to prevent scrollbars, communicating between frames via `postMessage`.
 *  4. Sets the preview width according to the chosen device mode,
 */
import { useEffect, useMemo } from 'react';
import { getCssVariable } from '../../../../../../../../scss/js-helpers/getCssVariable';
import { useBreakpoints } from '../../../../../../infra/dls/hooks/use-get-breakpoints';
// For readability a separate function, it is parsed and passed in the script to render the email correctly
var iframeContentSetupLogic = function (targetInnerEmailFrameId, campaignHtmlRaw, currentThemeMode, bgColorForOverride) {
    document.addEventListener('DOMContentLoaded', function () {
        var _a;
        var emailFrame = document.getElementById(targetInnerEmailFrameId);
        var parser = new DOMParser();
        var emailRenderDoc = parser.parseFromString(campaignHtmlRaw || '', 'text/html');
        // Remove any OS's meta tags related to theme, and mock with out "data-mode"
        emailRenderDoc.documentElement.setAttribute('data-mode', currentThemeMode);
        Array.from(emailRenderDoc.head.querySelectorAll('meta[name="color-scheme"], meta[name="supported-color-schemes"]')).forEach(function (meta) { return meta.remove(); });
        var htmlToWrite = new XMLSerializer().serializeToString(emailRenderDoc);
        var doc = emailFrame.contentDocument || ((_a = emailFrame.contentWindow) === null || _a === void 0 ? void 0 : _a.document);
        doc.open();
        doc.write(htmlToWrite);
        doc.close();
        // styles override to remove background & padding from received email html
        var styleTag = doc.createElement('style');
        styleTag.textContent = "\n        html, body { \n          scrollbar-width: none;\n        }\n        ::-webkit-scrollbar { \n          display: none; \n        }\n        .engage-v2-body {\n          padding: 0 !important;\n          background: ".concat(bgColorForOverride, " !important;\n        }\n      ");
        doc.head.appendChild(styleTag);
        // height calculation logic, to set the outer iframe to correct height to match the content
        var calculateAndUpdateHeight = function () {
            var finalHeight = Math.max(doc.body.scrollHeight, doc.body.offsetHeight, doc.documentElement.scrollHeight, doc.documentElement.offsetHeight) - 0.5;
            emailFrame.style.height = "".concat(finalHeight, "px");
            window.parent.postMessage({ type: 'resize', height: finalHeight, source: 'emailPreviewOuterToResize' }, '*');
        };
        (emailFrame.contentWindow || window).requestAnimationFrame(calculateAndUpdateHeight);
        var innerFrameWindow = emailFrame.contentWindow;
        if (innerFrameWindow && 'ResizeObserver' in innerFrameWindow && doc.body) {
            new innerFrameWindow.ResizeObserver(calculateAndUpdateHeight).observe(doc.body);
            // backup if ResizeObserver is not available
        }
        else if (doc.body && innerFrameWindow) {
            innerFrameWindow.addEventListener('load', calculateAndUpdateHeight, true);
        }
    });
};
export var usePrepareHtmlPreview = function (_a) {
    var campaignHtml = _a.campaignHtml, themeMode = _a.themeMode, deviceMode = _a.deviceMode, iframeRef = _a.iframeRef;
    var realBgColor = getCssVariable('--Background-Primary');
    var isMobileOrSmaller = useBreakpoints().isMobileOrSmaller;
    var containerHTML = useMemo(function () {
        // eslint-disable-next-line no-nested-ternary
        var viewWidth = isMobileOrSmaller ? 309 : deviceMode === 'mobile' ? 375 : 500;
        var innerIframeDOMId = 'emailFrame';
        var iframeLogicString = iframeContentSetupLogic.toString();
        var targetEmailFrameIdArg = JSON.stringify(innerIframeDOMId);
        var campaignHtmlArg = JSON.stringify(campaignHtml);
        var currentThemeModeArg = JSON.stringify(themeMode);
        var bgColorForOverrideArg = JSON.stringify(realBgColor);
        var scriptToExecuteInOuterIframe = "\n      (function() {\n        const setupFn = (".concat(iframeLogicString, ");\n\n        setupFn(\n          ").concat(targetEmailFrameIdArg, ",\n          ").concat(campaignHtmlArg, ",\n          ").concat(currentThemeModeArg, ",\n          ").concat(bgColorForOverrideArg, ",\n        );\n      })();\n    ");
        return "\n      <!DOCTYPE html>\n      <html>\n        <head>\n          <style>\n            html, body {\n              margin: 0;\n              padding: 0;\n              overflow: hidden; \n              background: transparent; \n              height: 100%; \n              scrollbar-width: none;\n            }\n\n            ::-webkit-scrollbar { \n              display: none; \n            }\n\n            #".concat(innerIframeDOMId, " {\n              width: ").concat(viewWidth, "px;\n              border: none;\n              overflow: hidden;\n            }\n          </style>\n        </head>\n        <body>\n          <iframe \n            id=\"").concat(innerIframeDOMId, "\" \n            sandbox=\"allow-scripts allow-same-origin\">\n          </iframe>\n          <script>").concat(scriptToExecuteInOuterIframe, "</script>\n        </body>\n      </html>\n    ");
    }, [campaignHtml, deviceMode, isMobileOrSmaller, realBgColor, themeMode]);
    useEffect(function () {
        var iframe = iframeRef.current;
        if (iframe) {
            iframe.srcdoc = containerHTML;
        }
        var handleMessage = function (event) {
            var _a, _b;
            if (iframe && ((_a = event.data) === null || _a === void 0 ? void 0 : _a.type) === 'resize' && ((_b = event.data) === null || _b === void 0 ? void 0 : _b.source) === 'emailPreviewOuterToResize') {
                iframe.style.height = "".concat(event.data.height, "px");
            }
        };
        window.addEventListener('message', handleMessage);
        return function () { return window.removeEventListener('message', handleMessage); };
    }, [containerHTML, iframeRef]);
};
